import type Highcharts from "highcharts"

const GAP = 4

type Box = { x: number; y: number; width: number; height: number }

const overlaps = (a: Box, b: Box) =>
  a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height

/** Each drawn column's rectangle in chart coordinates. */
const columnBoxes = (chart: Highcharts.Chart): Box[] =>
  chart.series
    .filter((s) => s.visible && s.type === "column")
    .flatMap((s) => s.points)
    .flatMap((p) => {
      const shape = (p as Highcharts.Point & { shapeArgs?: Partial<Box> }).shapeArgs
      if (shape?.x == null || shape.y == null || !shape.width || !shape.height) return []
      return [
        {
          x: chart.plotLeft + shape.x,
          y: chart.plotTop + shape.y,
          width: shape.width,
          height: shape.height,
        },
      ]
    })

/**
 * Moves a right-aligned plot line label left, past one column at a time, until no column is under
 * it. Highcharts re-aligns the label on every redraw, so call it from the chart's `render` event.
 * Leaves the label where Highcharts put it when no clear spot fits inside the plot.
 */
export const shiftPlotLineLabelsClearOfColumns = (chart: Highcharts.Chart) => {
  const columns = columnBoxes(chart)
  if (columns.length === 0) return
  for (const axis of chart.yAxis) {
    const bands = (axis as Highcharts.Axis & { plotLinesAndBands?: Highcharts.PlotLineOrBand[] })
      .plotLinesAndBands
    for (const band of bands ?? []) {
      const label = (band as Highcharts.PlotLineOrBand & { label?: Highcharts.SVGElement }).label
      if (!label) continue
      // Measured where Highcharts aligned it, not where the last render moved it.
      label.attr({ translateX: 0 })
      const start = label.getBBox()
      let box: Box = { ...start }
      for (;;) {
        const hit = columns.filter((c) => overlaps(box, c))
        if (hit.length === 0) break
        const left = Math.min(...hit.map((c) => c.x)) - GAP
        box = { ...box, x: left - box.width }
        if (box.x < chart.plotLeft) {
          box = start
          break
        }
      }
      if (box.x !== start.x) label.attr({ translateX: box.x - start.x })
    }
  }
}
